/**
 * 支出タブ。支出の追加・一覧・編集・削除。
 */
import {
  toExpenseRows,
  toFormState,
  useCreateExpense,
  useDeleteExpense,
  useUpdateExpense,
} from "@spli/app-logic";
import type { ExpenseInput, GroupDto } from "@spli/core";
import { useState } from "react";
import { AdSlot } from "../../components/AdSlot.js";
import { ExpenseForm } from "../../components/ExpenseForm.js";
import { EmptyState, ErrorView } from "../../components/StatusViews.js";
import { Icon } from "../../components/Icon.js";

export function ExpensesView({ group }: { group: GroupDto }) {
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const createExpense = useCreateExpense(group.shareToken);
  const updateExpense = useUpdateExpense(group.shareToken);
  const deleteExpense = useDeleteExpense(group.shareToken);

  const rows = toExpenseRows(group.expenses, group.members, group.currency);
  const editingExpense = group.expenses.find((e) => e.id === editingId);

  if (group.members.length === 0) {
    return (
      <EmptyState icon="members">
        まずは「メンバー」タブから参加者を追加してください。
      </EmptyState>
    );
  }

  const handleCreate = (input: ExpenseInput): void => {
    createExpense.mutate(input, { onSuccess: () => setAdding(false) });
  };

  // フォームを開いている間は一覧を出さない。
  // 入力中に下の一覧が動くと、どこを見ればよいか分からなくなるため。
  if (adding) {
    return (
      <section className="card">
        <h2 className="card__title">支出を追加</h2>
        <ExpenseForm
          group={group}
          submitLabel="追加する"
          pending={createExpense.isPending}
          onSubmit={handleCreate}
          onCancel={() => setAdding(false)}
        />
        {createExpense.isError && <ErrorView error={createExpense.error} />}
      </section>
    );
  }

  if (editingExpense) {
    return (
      <section className="card">
        <h2 className="card__title">支出を編集</h2>
        <ExpenseForm
          group={group}
          initialState={toFormState(editingExpense, group.currency)}
          submitLabel="保存する"
          pending={updateExpense.isPending}
          onSubmit={(input) =>
            updateExpense.mutate(
              { expenseId: editingExpense.id, input },
              { onSuccess: () => setEditingId(null) },
            )
          }
          onCancel={() => setEditingId(null)}
        />
        {updateExpense.isError && <ErrorView error={updateExpense.error} />}
      </section>
    );
  }

  return (
    <>
      <button type="button" className="button button--block" onClick={() => setAdding(true)}>
        ＋ 支出を追加
      </button>

      <section style={{ marginTop: 16 }}>
        {rows.length === 0 ? (
          <EmptyState icon="receipt">
            まだ支出がありません。
            <br />
            「支出を追加」から記録を始めましょう。
          </EmptyState>
        ) : (
          <div className="list">
            {rows.map((row, index) => (
              <div key={row.id}>
                {/*
                  カード全体を押すと編集。削除だけ別ボタンにして、
                  行の操作エリアを作らずに済ませている。
                */}
                <article className="expense-card">
                  <button
                    type="button"
                    className="expense-card__main"
                    onClick={() => setEditingId(row.id)}
                  >
                    <span className="list-item__body">
                      <span className="list-item__title">
                        {/*
                          カテゴリのタグ。アイコンだけだと何を指すか伝わらないので、
                          ラベルと並べて出す (アイコンは装飾扱いで読み上げない)。
                        */}
                        <span className="category-tag">
                          <Icon name={row.icon} size={14} />
                          {row.categoryLabel}
                        </span>
                        <span className="expense-card__name">{row.title}</span>
                      </span>
                      <span className="list-item__meta">
                        {/* 日付が未指定の支出では、日付の分だけ黙って省く */}
                        {row.occurredOn !== null && `${row.occurredOn} ・ `}
                        {row.payerNames} が立替 ・ {row.participantCount}
                        人で割り勘
                      </span>
                      {row.note && <span className="list-item__meta">{row.note}</span>}
                    </span>
                    <span className="list-item__amount">{row.amountLabel}</span>
                  </button>

                  <button
                    type="button"
                    className="icon-button"
                    aria-label={`${row.title} を削除`}
                    title="削除"
                    onClick={() => {
                      if (window.confirm(`「${row.title}」を削除しますか?`)) {
                        deleteExpense.mutate(row.id);
                      }
                    }}
                  >
                    <Icon name="trash" size={18} />
                  </button>
                </article>

                {/* 一覧の途中に広告を挿入する (収益モデル: 広告表示) */}
                {index === 2 && (
                  <AdSlot placement="expense-list" expenseCount={group.expenses.length} />
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
}
